/**
 * Play Mode Control Panel Component
 *
 * Dockable panel for controlling story playback during development/testing.
 * Features:
 * - Play/pause/restart controls
 * - Choice presentation and selection
 * - Variable inspector sidebar
 * - Step-back navigation
 * - Playthrough save/load
 * - Coverage and progress tracking
 *
 * The panel is designed to be side-by-side with the canvas during development,
 * allowing writers to test their story logic in real-time while editing.
 *
 * @module playMode/PlayModePanel
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { TraversalEngine } from './TraversalEngine';
import { VariableInspector } from './VariableInspector';
import type { TraversalResult, StoryGraph, PlaythroughState } from './TraversalEngine';
import { catalogStore, useCatalog } from '../../tilemap/store/catalogStore';

/**
 * Play Mode Panel Props
 *
 * Receives the story graph and callbacks for UI updates
 */
interface PlayModePanelProps {
  /** The story graph to play */
  graph: StoryGraph;

  /**
   * The project this story belongs to. Only needed so the current-node box
   * can show a `tilemap_ref` node's map name instead of its raw uuid.
   */
  projectUuid?: string;

  /** Callback when play mode is entered/exited */
  onPlayModeChange?: (isPlaying: boolean) => void;

  /** Callback to highlight the current node on the canvas */
  onNodeHighlight?: (nodeId: string) => void;

  /** Optional title for the panel */
  title?: string;
}

/**
 * Play Mode Panel Component
 *
 * Main playback control interface. Handles:
 * - Playthrough initialization and progression
 * - User choice presentation and selection
 * - Variable state inspection and modification
 * - Playthrough history and step-back
 * - Save/load playthrough functionality
 */
export const PlayModePanel: React.FC<PlayModePanelProps> = ({
  graph,
  projectUuid,
  onPlayModeChange,
  onNodeHighlight,
  title = 'Story Playback',
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [engine, setEngine] = useState<TraversalEngine | null>(null);
  const [currentResult, setCurrentResult] = useState<TraversalResult | null>(null);
  const [currentPlaythrough, setCurrentPlaythrough] = useState<PlaythroughState | null>(null);
  const [showVariables, setShowVariables] = useState(true);

  useEffect(() => {
    if (projectUuid) catalogStore.getState().ensure(projectUuid);
  }, [projectUuid]);

  const currentNode = currentResult ? graph.nodes.get(currentResult.currentNodeId) : undefined;
  const isTilemapRef = currentNode?.type === 'tilemap_ref';
  const tilemapUuid = isTilemapRef ? (currentNode?.data.tilemap_uuid as string | undefined) : undefined;
  const sceneMap = useCatalog((s) =>
    projectUuid && tilemapUuid ? s.byProject[projectUuid]?.find((m) => m.uuid === tilemapUuid) : undefined,
  );

  /**
   * Start a new playthrough
   *
   * Initializes the traversal engine and begins playback from the entry node.
   */
  const handleStartPlay = useCallback(() => {
    const entryNodeId = graph.entryNodeId || Array.from(graph.nodes.values())[0]?.id;
    if (!entryNodeId) {
      console.error('No entry node found');
      return;
    }

    const newEngine = new TraversalEngine(graph, {
      trackHistory: true,
      maxSteps: 10000,
    });

    const result = newEngine.startPlaythrough(entryNodeId);
    setEngine(newEngine);
    setCurrentResult(result);
    setCurrentPlaythrough(newEngine.getCurrentPlaythrough());
    setIsPlaying(true);
    onPlayModeChange?.(true);
    onNodeHighlight?.(result.currentNodeId);
  }, [graph, onPlayModeChange, onNodeHighlight]);

  /**
   * Stop playback and return to edit mode
   */
  const handleStopPlay = useCallback(() => {
    setIsPlaying(false);
    setEngine(null);
    setCurrentResult(null);
    setCurrentPlaythrough(null);
    onPlayModeChange?.(false);
    onNodeHighlight?.(undefined as any);
  }, [onPlayModeChange, onNodeHighlight]);

  /**
   * Make a choice and advance the story
   *
   * @param targetNodeId The node to advance to
   */
  const handleMakeChoice = useCallback(
    (targetNodeId: string) => {
      if (!engine) return;

      const result = engine.makeChoice(targetNodeId);
      setCurrentResult(result);
      setCurrentPlaythrough(engine.getCurrentPlaythrough());
      onNodeHighlight?.(result.currentNodeId);
    },
    [engine, onNodeHighlight]
  );

  /**
   * Step back to the previous choice
   *
   * Currently a placeholder — full implementation would restore state from history.
   */
  const handleStepBack = useCallback(() => {
    if (!currentPlaythrough || currentPlaythrough.steps.length === 0) return;

    // TODO: Implement step-back by replaying up to previous step
    console.log('Step-back not yet implemented');
  }, [currentPlaythrough]);

  /**
   * Save the current playthrough to the database
   */
  const handleSavePlaythrough = useCallback(() => {
    if (!currentPlaythrough) return;

    const name = prompt('Playthrough name:', currentPlaythrough.name);
    if (name) {
      currentPlaythrough.name = name;
      // TODO: POST to server to save playthrough
      console.log('Saving playthrough:', currentPlaythrough);
    }
  }, [currentPlaythrough]);

  /**
   * Calculate coverage percentage
   */
  const coverage = useMemo(() => {
    if (!engine) return 0;
    return Math.round(engine.getCoverage());
  }, [engine]);

  if (!isPlaying) {
    // Play mode off - show start button
    return (
      <div className="border-l border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-800 h-full flex flex-col gap-4">
        <h2 className="font-bold text-lg">{title}</h2>
        <button
          onClick={handleStartPlay}
          className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium"
        >
          ▶ Start Playback
        </button>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Start a new playthrough to test your story.
        </p>
      </div>
    );
  }

  // Play mode on - show playback interface
  return (
    <div className="border-l border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-800 h-full flex flex-col gap-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="font-bold text-lg">{title}</h2>
        <div className="flex gap-2">
          <button
            onClick={handleStepBack}
            disabled={!currentPlaythrough || currentPlaythrough.steps.length === 0}
            title="Step back"
            className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ⬅️
          </button>
          <button
            onClick={handleStopPlay}
            title="Stop playback"
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900 rounded text-red-600"
          >
            ⏹️
          </button>
        </div>
      </div>

      <hr className="border-slate-300 dark:border-slate-600" />

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-sm font-medium">Story Progress</span>
          <span className="text-xs bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded">
            {currentPlaythrough?.steps.length || 0} steps
          </span>
        </div>
        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded h-2">
          <div
            className="bg-blue-500 h-2 rounded"
            style={{ width: `${coverage}%` }}
          />
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          {coverage}% coverage
        </p>
      </div>

      {/* Current Node Info */}
      {currentResult && (
        <div className="p-3 bg-slate-100 dark:bg-slate-900 rounded">
          <p className="text-sm font-medium mb-1">Current Node</p>
          {isTilemapRef ? (
            <p className="text-sm text-blue-600 dark:text-blue-400">
              🗺️ {sceneMap?.name ?? 'Tile Map'}
            </p>
          ) : (
            <p className="text-sm font-mono text-blue-600 dark:text-blue-400">
              {currentResult.currentNodeId}
            </p>
          )}
        </div>
      )}

      {/* Choices */}
      {currentResult?.choices && currentResult.choices.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Available Choices</p>
          <div className="max-h-48 overflow-y-auto space-y-1">
            {currentResult.choices.map((choice) => (
              <button
                key={choice.targetId}
                onClick={() => handleMakeChoice(choice.targetId)}
                disabled={!choice.conditionMet}
                title={!choice.conditionMet ? 'Condition not met' : ''}
                className={`w-full text-left px-3 py-2 rounded text-sm ${
                  choice.conditionMet
                    ? 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600'
                    : 'bg-slate-100 dark:bg-slate-700 opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex justify-between items-center gap-2">
                  <span className="flex-1 break-words">{choice.label}</span>
                  {!choice.conditionMet && (
                    <span className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-200 px-1 rounded">
                      ✗
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <hr className="border-slate-300 dark:border-slate-600" />

      {/* Variable Inspector Toggle */}
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium">Variables</span>
        <button
          onClick={() => setShowVariables(!showVariables)}
          className="text-xs px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded hover:bg-slate-300 dark:hover:bg-slate-600"
        >
          {showVariables ? 'Hide' : 'Show'}
        </button>
      </div>

      {/* Variable Inspector */}
      {showVariables && currentPlaythrough && (
        <div className="flex-1 overflow-hidden">
          <VariableInspector variables={currentPlaythrough.variables} />
        </div>
      )}

      {/* Bottom actions */}
      <button
        onClick={handleSavePlaythrough}
        className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium mt-auto"
      >
        💾 Save Run
      </button>
    </div>
  );
};

PlayModePanel.displayName = 'PlayModePanel';
